import type { PresentationData } from '../types'
import {
  EnhancedTitleSlide,
  EnhancedAgendaSlide,
  EnhancedFeatureGridSlide,
  EnhancedSectionDividerSlide,
  EnhancedComparisonSlide,
  EnhancedTeamSlide,
  ContentSlideLayout,

} from '../components/slides'
import { IconSolid } from '../components/ui'

/**
 * PROFESSIONAL SLIDE LAYOUTS DEMO
 * 
 * This presentation demonstrates the new professional slide layouts
 * with consistent branding, modern design, and visual variety.
 */

export const professionalLayoutsDemo: PresentationData = {
  metadata: {
    title: 'Professional Slide Layouts Demo',
    description: 'Showcasing modern, professional presentation layouts',
    author: 'Design Team',
    date: 'Oktober 2025',
    tags: ['Design System', 'Layouts', 'Professional', 'Demo'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Enhanced Title Slide
    // ============================================
    {
      id: 1,
      component: () => (
        <EnhancedTitleSlide
          title="Professional Presentations"
          subtitle="Modern, consistent, and visually appealing slide layouts for professional presentations"
          author="Design & Development Team"
          date="Oktober 2025"
          company="Präsentationen Inc."
          tags={['Modern Design', 'Consistent Branding', 'Professional']}
          variant="primary"
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: Section Divider
    // ============================================
    {
      id: 2,
      component: () => (
        <EnhancedSectionDividerSlide
          title="Design Principles"
          subtitle="The foundation of our professional slide system"
          sectionNumber={1}
          icon={<IconSolid name="lightbulb" size="3xl" />}
          variant="secondary"
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 3: Enhanced Feature Grid
    // ============================================
    {
      id: 3,
      component: () => (
        <EnhancedFeatureGridSlide
          title="Key Design Features"
          subtitle="What makes our slide layouts professional and modern"
          columns={3}
          variant="light"
          features={[
            {
              icon: <IconSolid name="palette" size="2xl" />,
              title: "Consistent Branding",
              description: "Professional color schemes and typography that maintains visual identity across all slides.",
              items: [
                "Primary, secondary, and accent color palettes",
                "Consistent typography hierarchy",
                "Professional spacing and margins"
              ]
            },
            {
              icon: <IconSolid name="layout" size="2xl" />,
              title: "Multiple Layouts",
              description: "Variety of slide layouts for different content types and presentation needs.",
              items: [
                "Title slides with branding",
                "Content slides with flexible layouts", 
                "Grid layouts for features",
                "Two-column comparison layouts"
              ],
              highlight: true
            },
            {
              icon: <IconSolid name="sparkles" size="2xl" />,
              title: "Modern Animations",
              description: "Subtle animations and transitions that enhance user experience without distraction.",
              items: [
                "Smooth fade-in animations",
                "Staggered element appearances",
                "Hover effects on interactive elements"
              ]
            }
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 4: Enhanced Agenda
    // ============================================
    {
      id: 4,
      component: () => (
        <EnhancedAgendaSlide
          title="Presentation Structure"
          subtitle="How we organize content for maximum impact"
          variant="light"
          items={[
            {
              number: 1,
              title: "Title & Introduction",
              description: "Professional title slides with branding and clear messaging",
              duration: "2-3 slides",
              items: ["Main title slide", "Section dividers", "Overview slides"]
            },
            {
              number: 2, 
              title: "Content Sections",
              description: "Structured content using consistent layouts and visual hierarchy",
              duration: "10-15 slides",
              items: ["Feature grids", "Comparison slides", "Content layouts"]
            },
            {
              number: 3,
              title: "Team & Conclusion",
              description: "Professional closing with team information and next steps",
              duration: "2-3 slides", 
              items: ["Team introduction", "Contact information", "Call to action"]
            }
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 5: Comparison Demo
    // ============================================
    {
      id: 5,
      component: () => (
        <EnhancedComparisonSlide
          title="Before vs. After"
          subtitle="See the difference professional layouts make"
          leftTitle="Old Approach"
          rightTitle="Professional Layouts"
          variant="light"
          leftContent={
            <div className="space-y-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded border-2 border-dashed border-gray-300">
                <h4 className="font-bold text-red-600 mb-2">Issues with old slides:</h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li>• Inconsistent spacing and typography</li>
                  <li>• No unified color scheme</li>
                  <li>• Poor visual hierarchy</li>
                  <li>• Generic, unprofessional appearance</li>
                  <li>• Navigation overlapping content</li>
                </ul>
              </div>
            </div>
          }
          rightContent={
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded border-2 border-green-200 dark:border-green-700">
                <h4 className="font-bold text-green-600 mb-2">Professional advantages:</h4>
                <ul className="space-y-2 text-sm text-green-700 dark:text-green-300">
                  <li>• Consistent branding and visual identity</li>
                  <li>• Modern, clean design aesthetic</li>
                  <li>• Clear visual hierarchy and typography</li>
                  <li>• Multiple layout options for variety</li>
                  <li>• Proper spacing and navigation</li>
                </ul>
              </div>
            </div>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 6: Team Slide Demo
    // ============================================
    {
      id: 6,
      component: () => (
        <EnhancedTeamSlide
          title="Meet the Design Team"
          subtitle="The people behind the professional presentation system"
          variant="light"
          members={[
            {
              name: "Sarah Johnson",
              role: "Lead UX Designer",
              email: "sarah@presentations.com",
              social: [
                { platform: "LinkedIn", url: "#", icon: <IconSolid name="linkedin" /> },
                { platform: "Twitter", url: "#", icon: <IconSolid name="twitter" /> }
              ]
            },
            {
              name: "Michael Chen", 
              role: "Frontend Developer",
              email: "michael@presentations.com",
              social: [
                { platform: "GitHub", url: "#", icon: <IconSolid name="github" /> },
                { platform: "LinkedIn", url: "#", icon: <IconSolid name="linkedin" /> }
              ]
            },
            {
              name: "Emma Rodriguez",
              role: "Visual Designer", 
              email: "emma@presentations.com",
              social: [
                { platform: "Dribbble", url: "#", icon: <IconSolid name="dribbble" /> },
                { platform: "Behance", url: "#", icon: <IconSolid name="behance" /> }
              ]
            }
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 7: Final Slide - Call to Action
    // ============================================
    {
      id: 7,
      component: () => (
        <ContentSlideLayout
          title="Start Using Professional Layouts"
          subtitle="Ready to create impressive presentations?"
          variant="dark"
          layout="centered"
        >
          <div className="text-center space-y-8 max-w-4xl mx-auto">
            <div className="text-6xl mb-6">
              🚀
            </div>
            <h3 className="text-3xl font-bold mb-6">
              Transform Your Presentations Today
            </h3>
            <p className="text-xl opacity-90 leading-relaxed mb-8">
              Use our professional slide layouts to create consistent, modern, and visually appealing presentations that leave a lasting impression.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30">
                <IconSolid name="rocket" size="2xl" className="mx-auto mb-4" />
                <h4 className="font-bold mb-2">Get Started</h4>
                <p className="text-sm opacity-90">Import the layouts and start creating</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30">
                <IconSolid name="book" size="2xl" className="mx-auto mb-4" />
                <h4 className="font-bold mb-2">Learn More</h4>
                <p className="text-sm opacity-90">Explore all available components</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30">
                <IconSolid name="users" size="2xl" className="mx-auto mb-4" />
                <h4 className="font-bold mb-2">Get Support</h4>
                <p className="text-sm opacity-90">Reach out to the design team</p>
              </div>
            </div>
          </div>
        </ContentSlideLayout>
      ),
      transition: 'fade',
    },
  ],
}